<script setup lang="ts">
// Global social-stream overlay, mounted in the layout so it appears on every
// page and its open/closed state + connection persist across route changes. It's
// a fixed-position overlay that never participates in page layout or navigation.
const { user } = useAuth()
const { fmtDateTime } = useClientFormat()
const bridge = useNativeBridge()
const {
  open, messages, enabled, transport,
  ensureStarted, teardown, send, adminDelete, adminCensor,
  adminUserInfo, adminPurgeUser, adminMute, adminUnmute,
} = useChat()

const isAdmin = computed(() => !!user.value?.is_admin)
const canPost = computed(() => !!user.value)

const draft = ref('')
const sending = ref(false)
const errorMsg = ref('')
const MAX = 280
const remaining = computed(() => MAX - draft.value.length)
const bodyEl = ref<HTMLElement | null>(null)

function scrollToBottom() {
  nextTick(() => { const el = bodyEl.value; if (el) el.scrollTop = el.scrollHeight })
}
watch(() => messages.value.length, scrollToBottom)
watch(open, (v) => { if (v) scrollToBottom() })
onMounted(() => {
  // Connect the social stream only for signed-in users — never on /login. Start
  // it when a user appears (after login) and tear it down on logout, so the chat
  // endpoints (/chat/messages, /chat/stream) are never hit unauthenticated. The
  // watch lives inside onMounted so it only runs on the client.
  watch(user, (u) => { if (u) ensureStarted(); else teardown() }, { immediate: true })
  // OS back closes the open chat before anything else (registered last → runs first).
  if (bridge.isNative.value) {
    const off = bridge.registerBack(() => { if (open.value) { open.value = false; return true } return false })
    onBeforeUnmount(off)
  }
})

function toggle() { open.value = !open.value }

async function doSend() {
  const text = draft.value.trim()
  if (!text || sending.value) return
  sending.value = true
  errorMsg.value = ''
  const err = await send(text)
  if (err) errorMsg.value = err
  else draft.value = ''
  sending.value = false
}

// ---- admin: word -> censor popover ----
const wordMenu = ref<{ word: string; x: number; y: number } | null>(null)
function onWordClick(ev: MouseEvent, word: string) {
  if (!isAdmin.value) return
  const clean = word.replace(/[^\p{L}\p{N}*]/gu, '')
  if (!clean || clean.includes('*')) return
  wordMenu.value = { word: clean, x: ev.clientX, y: ev.clientY }
}
async function confirmCensor() {
  if (!wordMenu.value) return
  await adminCensor(wordMenu.value.word)
  wordMenu.value = null
}
function words(body: string) { return body.split(/(\s+)/) }

// ---- admin: delete-one confirm modal ----
const delTarget = ref<any | null>(null)   // the message pending delete
function askDelete(m: any) { delTarget.value = m }
async function confirmDelete() {
  if (delTarget.value) await adminDelete(delTarget.value.id)
  delTarget.value = null
}

// ---- admin: per-user moderation modal ----
const mod = ref<{ userId: number; name: string } | null>(null)
const modInfo = ref<any | null>(null)
const modBusy = ref(false)
const modConfirmPurge = ref(false)
async function openMod(m: any) {
  if (!m.user_id) return
  mod.value = { userId: m.user_id, name: m.name }
  modInfo.value = null
  modConfirmPurge.value = false
  modInfo.value = await adminUserInfo(m.user_id)
}
function closeMod() { mod.value = null; modInfo.value = null }
async function doMute(minutes: number | null) {
  if (!mod.value) return
  modBusy.value = true
  await adminMute(mod.value.userId, minutes)
  modInfo.value = await adminUserInfo(mod.value.userId)
  modBusy.value = false
}
async function doUnmute() {
  if (!mod.value) return
  modBusy.value = true
  await adminUnmute(mod.value.userId)
  modInfo.value = await adminUserInfo(mod.value.userId)
  modBusy.value = false
}
async function doPurge() {
  if (!mod.value) return
  modBusy.value = true
  await adminPurgeUser(mod.value.userId)
  modInfo.value = await adminUserInfo(mod.value.userId)
  modConfirmPurge.value = false
  modBusy.value = false
}
function muteLabel(info: any): string {
  if (!info?.mute) return ''
  if (info.mute.banned) return 'banned from chat'
  return `timed out until ${fmtDateTime(info.mute.until)}`
}
</script>

<template>
  <div class="chat-mount">
    <button class="chat-fab" :class="{ open }" aria-label="social stream" @click="toggle">
      <span v-if="!open" class="fab-ico">💬</span>
      <span v-else class="fab-ico">×</span>
      <span v-if="!open && transport === 'live'" class="fab-dot"></span>
    </button>

    <transition name="chat-slide">
      <section v-show="open" class="chat-panel" role="dialog" aria-label="social stream">
        <header class="chat-head">
          <div class="title">
            social stream
            <span class="conn" :class="transport">
              {{ transport === 'live' ? 'live' : transport === 'polling' ? 'updating' : transport === 'off' ? 'off' : '…' }}
            </span>
          </div>
          <button class="x" aria-label="close" @click="open = false">×</button>
        </header>

        <div ref="bodyEl" class="chat-body">
          <div v-if="!messages.length" class="empty">no messages yet — say hello 👋</div>
          <div v-for="m in messages" :key="m.id" class="msg" :class="{ system: m.kind === 'system' }">
            <div class="msg-line">
              <span v-if="m.kind === 'system'" class="bot">🤖 BuzzBetBot</span>
              <span v-else class="name">{{ m.name }}</span>
              <span class="body">
                <template v-if="isAdmin && m.kind !== 'system'">
                  <span v-for="(w, i) in words(m.body)" :key="i"
                    :class="{ word: w.trim() }" @click="onWordClick($event, w)">{{ w }}</span>
                </template>
                <template v-else>{{ m.body }}</template>
              </span>
              <span v-if="isAdmin && m.kind !== 'system'" class="mod-actions">
                <button class="micon" title="moderate user" @click="openMod(m)">⚙</button>
                <button class="micon" title="delete message" @click="askDelete(m)">🗑</button>
              </span>
            </div>
            <span v-if="m.created_at" class="ts">{{ fmtDateTime(m.created_at) }}</span>
          </div>
        </div>

        <div v-if="errorMsg" class="chat-err">{{ errorMsg }}</div>

        <div v-if="!enabled" class="chat-foot muted">the stream is disabled by an admin</div>
        <div v-else-if="canPost" class="composer">
          <div class="input-wrap">
            <input v-model="draft" class="fld msg-input" :maxlength="MAX"
              placeholder="message…" @keyup.enter="doSend" />
            <span class="count" :class="{ low: remaining < 20 }">{{ remaining }}</span>
          </div>
          <button class="send" :disabled="sending || !draft.trim()" aria-label="send" @click="doSend">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4 20-7z" />
            </svg>
          </button>
        </div>
        <div v-else class="chat-foot muted">
          <NuxtLink class="btn-link" to="/login">sign in</NuxtLink> to chat — anyone can read.
        </div>

        <!-- admin: add-to-censor popover -->
        <div v-if="wordMenu" class="word-pop" :style="{ left: wordMenu.x + 'px', top: wordMenu.y + 'px' }">
          <span>censor “{{ wordMenu.word }}”?</span>
          <button class="btn-ghost sm" @click="confirmCensor">add</button>
          <button class="btn-link sm" @click="wordMenu = null">cancel</button>
        </div>

        <!-- admin: delete-one confirmation modal -->
        <div v-if="delTarget" class="modal-scrim" @click.self="delTarget = null">
          <div class="modal">
            <div class="modal-h">Remove this message?</div>
            <div class="modal-quote">
              <span class="q-name">{{ delTarget.name }}</span>
              <span class="q-body">{{ delTarget.body }}</span>
            </div>
            <div class="modal-actions">
              <button class="btn-ghost sm" @click="delTarget = null">cancel</button>
              <button class="btn-primary sm danger" @click="confirmDelete">delete</button>
            </div>
          </div>
        </div>

        <!-- admin: per-user moderation modal -->
        <div v-if="mod" class="modal-scrim" @click.self="closeMod">
          <div class="modal wide">
            <div class="modal-h">
              moderate <b>{{ mod.name }}</b>
              <button class="x sm" @click="closeMod">×</button>
            </div>
            <div v-if="modInfo?.mute" class="mute-banner">{{ muteLabel(modInfo) }}</div>

            <div class="mod-grid">
              <button class="btn-ghost sm" :disabled="modBusy" @click="doMute(10)">timeout 10m</button>
              <button class="btn-ghost sm" :disabled="modBusy" @click="doMute(60)">timeout 1h</button>
              <button class="btn-ghost sm" :disabled="modBusy" @click="doMute(1440)">timeout 24h</button>
              <button class="btn-primary sm danger" :disabled="modBusy" @click="doMute(null)">ban</button>
              <button v-if="modInfo?.mute" class="btn-ghost sm" :disabled="modBusy" @click="doUnmute">lift mute</button>
            </div>

            <div class="mod-purge">
              <template v-if="!modConfirmPurge">
                <button class="btn-ghost sm danger" :disabled="modBusy" @click="modConfirmPurge = true">
                  delete all messages from this user
                </button>
              </template>
              <template v-else>
                <span class="purge-q">delete every message from {{ mod.name }}?</span>
                <button class="btn-ghost sm" @click="modConfirmPurge = false">cancel</button>
                <button class="btn-primary sm danger" :disabled="modBusy" @click="doPurge">delete all</button>
              </template>
            </div>

            <div class="mod-history">
              <div class="hist-h">recent messages</div>
              <div v-if="!modInfo" class="muted sm">loading…</div>
              <div v-else-if="!modInfo.messages?.length" class="muted sm">no messages.</div>
              <div v-else class="hist-list">
                <div v-for="hm in modInfo.messages" :key="hm.id" class="hist-row">
                  <span class="hist-body">{{ hm.body }}</span>
                  <span class="hist-ts">{{ fmtDateTime(hm.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </transition>
  </div>
</template>

<style scoped>
.chat-mount { position: fixed; inset: 0; pointer-events: none; z-index: 130; }
.chat-fab, .chat-panel, .word-pop, .modal-scrim { pointer-events: auto; }

.chat-fab {
  position: fixed; right: 20px; bottom: calc(20px + var(--bt-bottom-safe, 0px)); width: 54px; height: 54px; border-radius: 50%;
  border: 1px solid var(--line); background: linear-gradient(140deg, var(--brand), var(--brand-2));
  color: #fff; font-size: 22px; cursor: pointer; box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center;
}
.chat-fab.open { background: var(--panel); color: var(--ink); }
.chat-fab .fab-dot { position: absolute; top: 10px; right: 12px; width: 9px; height: 9px;
  border-radius: 50%; background: var(--accent); border: 2px solid var(--bg); }

.chat-panel {
  position: fixed; right: 20px; bottom: calc(86px + var(--bt-bottom-safe, 0px)); width: 380px; max-width: calc(100vw - 32px);
  height: min(560px, calc(100dvh - 120px)); background: var(--panel);
  border: 1px solid var(--line); border-radius: 16px; box-shadow: 0 14px 40px rgba(0,0,0,0.5);
  display: flex; flex-direction: column; overflow: hidden;
}
.chat-head { display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px; border-bottom: 1px solid var(--line); }
.chat-head .title { font-weight: 800; font-size: 14px; display: flex; align-items: center; gap: 9px; }
.conn { font-family: var(--font-mono); font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--muted); border: 1px solid var(--line); border-radius: 5px; padding: 1px 5px; }
.conn.live { color: var(--accent); border-color: var(--accent); }
.conn.polling { color: var(--gold); }
.x { background: none; border: none; color: var(--muted); font-size: 22px; cursor: pointer; line-height: 1; }
.x.sm { font-size: 18px; }

.chat-body { flex: 1; overflow-y: auto; padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; }
.empty { color: var(--muted); text-align: center; margin: auto; font-size: 13px; }
.msg { font-size: 13.5px; line-height: 1.4; }
.msg-line { display: flex; align-items: baseline; gap: 6px; }
.msg .name { font-weight: 700; color: var(--brand); flex-shrink: 0; }
.msg .bot { font-weight: 800; color: var(--brand-2); flex-shrink: 0; }
.msg .body { color: var(--ink); word-break: break-word; flex: 1; }
.msg.system { background: linear-gradient(180deg, rgba(111,41,224,0.12), rgba(47,210,198,0.05));
  border: 1px solid var(--line); border-radius: 9px; padding: 6px 9px; }
.msg .ts { font-family: var(--font-mono); font-size: 9px; color: var(--muted); }
.mod-actions { display: inline-flex; gap: 4px; flex-shrink: 0; align-items: center; }
.micon { background: none; border: none; cursor: pointer; opacity: 0.6; font-size: 17px;
  line-height: 1; padding: 2px 3px; border-radius: 6px; }
.micon:hover { opacity: 1; background: rgba(255,255,255,0.07); }
.word { cursor: pointer; border-radius: 3px; }
.word:hover { background: rgba(255,93,114,0.25); }

.chat-err { padding: 6px 14px; color: var(--warn); font-size: 12px; }
.chat-foot { padding: 12px 14px; border-top: 1px solid var(--line); font-size: 13px; text-align: center; }
.chat-foot.muted { color: var(--muted); }
.muted { color: var(--muted); }
.sm { font-size: 12px; }

/* composer: input grows; char count sits INSIDE the field; send is a centered
   square the SAME height as the input, with their centres aligned. */
.composer { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-top: 1px solid var(--line); }
.input-wrap { position: relative; flex: 1 1 auto; min-width: 0; display: flex; align-items: center; }
.msg-input { width: 100%; height: 42px; box-sizing: border-box; padding-right: 34px; margin: 0; }
.count { position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  font-family: var(--font-mono); font-size: 9px; color: var(--muted); pointer-events: none; }
.count.low { color: var(--warn); }
.send { flex: 0 0 auto; width: 42px; height: 42px; box-sizing: border-box; margin: 0;
  border-radius: 10px; border: 1px solid var(--line);
  background: linear-gradient(140deg, var(--brand), var(--brand-2)); color: #fff; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; }
.send:disabled { opacity: 0.5; cursor: default; }

.word-pop { position: fixed; transform: translate(-50%, calc(-100% - 8px)); background: var(--panel);
  border: 1px solid var(--line); border-radius: 9px; padding: 8px 10px; display: flex; align-items: center; gap: 8px;
  font-size: 12px; color: var(--ink); box-shadow: 0 8px 24px rgba(0,0,0,0.5); white-space: nowrap; z-index: 5; }
.word-pop .sm { padding: 3px 9px; width: auto; }

/* moderation / confirm modals (within the panel) */
.modal-scrim { position: absolute; inset: 0; background: rgba(8,4,20,0.6); display: flex;
  align-items: center; justify-content: center; padding: 14px; z-index: 6; }
.modal { background: var(--panel); border: 1px solid var(--line); border-radius: 13px;
  padding: 14px; width: 100%; max-width: 320px; box-shadow: 0 12px 36px rgba(0,0,0,0.55); }
.modal.wide { max-width: 340px; }
.modal-h { font-weight: 800; font-size: 14px; margin-bottom: 10px; display: flex;
  align-items: center; justify-content: space-between; }
.modal-quote { background: var(--bg); border: 1px solid var(--line); border-radius: 9px;
  padding: 9px 11px; margin-bottom: 12px; font-size: 13px; }
.q-name { font-weight: 700; color: var(--brand); margin-right: 7px; }
.q-body { color: var(--ink); word-break: break-word; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
.btn-primary.sm, .btn-ghost.sm { width: auto; height: 34px; padding: 0 14px; display: inline-flex;
  align-items: center; justify-content: center; }
.btn-primary.danger { background: var(--warn); border-color: var(--warn); }
.btn-ghost.danger { color: var(--warn); border-color: var(--warn); }
.mute-banner { background: rgba(255,93,114,0.12); border: 1px solid var(--warn); color: var(--warn);
  border-radius: 8px; padding: 6px 10px; font-size: 12px; margin-bottom: 10px; }
.mod-grid { display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 12px; }
.mod-purge { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 12px; }
.purge-q { font-size: 12px; color: var(--warn); }
.mod-history .hist-h { font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em;
  color: var(--muted); margin-bottom: 6px; }
.hist-list { max-height: 160px; overflow-y: auto; display: flex; flex-direction: column; gap: 5px; }
.hist-row { display: flex; justify-content: space-between; gap: 8px; font-size: 12px; }
.hist-body { color: var(--ink); word-break: break-word; }
.hist-ts { font-family: var(--font-mono); font-size: 9px; color: var(--muted); flex-shrink: 0; }

.chat-slide-enter-active, .chat-slide-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.chat-slide-enter-from, .chat-slide-leave-to { opacity: 0; transform: translateY(12px); }

@media (max-width: 480px) {
  /* the embed always renders at mobile width, so these overrides must re-add
     --bt-bottom-safe (like the base rules) or the FAB/panel sit at the raw
     viewport bottom and clip under the native floating tab bar. */
  .chat-panel { right: 12px; left: 12px; width: auto; bottom: calc(80px + var(--bt-bottom-safe, 0px)); }
  .chat-fab { right: 14px; bottom: calc(14px + var(--bt-bottom-safe, 0px)); }
}
</style>
